import React from "react";
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Image,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import FeatureCard from "../../components/ProductDetails/ProductFeatureCard";
import CommonFeatureCard from "../../components/Common/FeatureCard";
import ServiceCard from "../../components/Common/ServiceCard";

const OSSPPage = () => {
  const navigate = useNavigate();

  // Core Features Data
  const coreFeatures = [
    {
      icon: "/svg/product_gate.svg",
      title: "OSSPID Single Sign-On",
      description:
        "Centralized, secure authentication using email/password or third-party SSO",
    },
    {
      icon: "/svg/product_setting.svg",
      title: "Workflow Engine",
      description:
        "Configurable, dynamic workflows to automate form-based applications across sectors",
    },
    {
      icon: "/svg/product_grid.svg",
      title: "Real-Time Dashboard",
      description:
        "Insightful metrics, SLA monitoring, and service utilization tracking",
    },
    {
      icon: "/svg/product_hand_setting.svg",
      title: "Report Engine Online Payment Gateway",
      description:
        "Customizable reports with scheduled exports & filtering. Secure, multi-channel payment integration with local and international gateways",
    },
    {
      icon: "/svg/product_files.svg",
      title: "Document Management",
      description:
        "Supports QR codes, barcodes, digital signatures, with blockchain audit logs",
    },
    {
      icon: "/svg/product_group.svg",
      title: "Stakeholder Integration Meeting & Training Modules",
      description:
        "APIs for NID, Passport, UDC, and agency systems. Built-in scheduling tools to support stakeholder training and coordination",
    },
    {
      icon: "/svg/product_notification.svg",
      title: "Notification System",
      description: "Automated alerts via SMS, email, dashboards.",
    },
  ];

  // Why Choose OSS Platform Features Data
  const whyChooseFeatures = [
    {
      id: 1,
      title: "Proven Implementations",
      description:
        "Live platforms at Bangladesh Investment Development Authority and few other IPAs, serving 10,000+ users and processing over 150,000 transactions",
    },
    {
      id: 2,
      title: "Nationwide Support Network",
      description:
        "In-field teams ensure rollout and operations across Bangladesh",
    },
    {
      id: 3,
      title: "Third-Party Integrations",
      description:
        "Includes identity, payment gateway, G2B/ G2C systems, eNothi—ready for donor funded or government ecosystems.",
    },
    {
      id: 4,
      title: "Strategic Bank Partnerships",
      description:
        "Integrated with commercial banks for seamless payment flows.",
    },
    {
      id: 5,
      title: "Recognized Excellence",
      description:
        'Winner of the "Open Digital Government" award at WCIT 2017.',
    },
  ];

  // Ideal For Services Data
  const idealForServices = [
    {
      id: 1,
      title: "Nationwide e-Governance portals",
      description: "",
      icon: "/svg/support_service_6.svg",
      bgColor: "#E3F2FD",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title:
        "Investor onboarding systems for Hi-Tech parks, SEZs, or industrial authorities",
      description: "",
      icon: "/svg/support_service_2.svg",
      bgColor: "#E8F5E8",
      iconColor: "#2E7D32",
    },
    {
      id: 3,
      title: "Licensing, permit, and certification platforms",
      description: "",
      icon: "/svg/support_service_3.svg",
      bgColor: "#FFF3E0",
      iconColor: "#F57C00",
    },
    {
      id: 4,
      title: "Multi-agency service centers like OSS/ BIDA/BSCIC",
      description: "",
      icon: "/svg/support_service_5.svg",
      bgColor: "#FCE4EC",
      iconColor: "#C2185B",
    },
    {
      id: 5,
      title:
        "Donor-funded workflow automation and citizen digital inclusion projects",
      description: "",
      icon: "/svg/support_service_1.svg",
      bgColor: "#F3E5F5",
      iconColor: "#7B1FA2",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>One Stop Service Platform (OSSP) | Secure eGovernment Framework – Business Automation</title>
        <meta 
          name="description" 
          content="Explore OSS Platform — Business Automation's secure, AI-ready One Stop Service Platform built for e-Government, service automation, and transparent citizen engagement." 
        />
        <meta 
          name="keywords" 
          content="OSS platform Bangladesh, One Stop Service Platform, eGovernment workflow automation, OSSPID SSO Bangladesh, AI-ready government platform, blockchain audit logs Bangladesh, government portal solution, investor service portal Bangladesh, Workflow Engine OSSP, payment-integrated OSSP" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/products/ossp" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/products/ossp" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="One Stop Service Platform (OSSP) | Secure eGovernment Framework – Business Automation" />
        <meta property="og:description" content="Explore OSS Platform — Business Automation's secure, AI-ready One Stop Service Platform built for e-Government, service automation, and transparent citizen engagement." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/products/ossp" />
        <meta name="twitter:title" content="One Stop Service Platform (OSSP) | Secure eGovernment Framework – Business Automation" />
        <meta name="twitter:description" content="Explore OSS Platform — Business Automation's secure, AI-ready One Stop Service Platform built for e-Government, service automation, and transparent citizen engagement." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <Box
        py={6}
        // minH="100vh"
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
      }}
    >
      {/* Breadcrumb Section */}
      <Box>
        <Container maxW="8xl" px={{ base: 4, md: 6 }}>
          <Breadcrumb
            spacing="8px"
            separator={<ChevronRightIcon color="gray.500" />}
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                color="#000B8C"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate("/")}
                cursor="pointer"
              >
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <Text color="gray.600">OSSP</Text>
            </BreadcrumbItem>
          </Breadcrumb>
        </Container>
      </Box>

      <Container maxW="7xl" py={{ base: 4, md: 6 }}>
        <HStack align="center" wrap="wrap">
          <VStack
            align="start"
            spacing={0}
            flex={{ base: 1, lg: 3 }}
            minW={{ base: "100%", md: "500px", lg: "700px" }}
          >
            <Image
              src="/png/ossp_logo.png"
              alt="OSSP Logo"
              height={{ base: "40px", md: "56px" }}
              objectFit="contain"
              loading="lazy"
              fallbackSrc="/png/ossp_logo.png"
            />

            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="#000B8C"
              mt={4}
              lineHeight="1.2"
            >
              One Stop Service Platform{" "}
              <Text as="span" color="gray.700">
                (OSS Platform)
              </Text>
            </Heading>

            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.600"
              fontWeight="medium"
              pt={0}
              fontStyle={"italic"}
            >
              Streamlining e-Governance with Secure, AI-Ready Platforms
            </Text>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.700"
              lineHeight="1.2"
              textAlign="justify"
              mt={4}
              maxW={{ base: "100%", md: "700px", lg: "800px" }}
            >
              OSS Platform is our flagship integrated digital solution designed
              to consolidate end-to-end service delivery in one secure,
              scalable, and intelligent interface. Ideal for public agencies and
              enterprises, OSSP automates forms, implements dynamic workflows,
              enforces multi-layer authentication, and provides audit-grade
              transparency across services.
            </Text>
          </VStack>

          <Box
            flex={{ base: 1, lg: 1 }}
            minW={{ base: "100%", md: "350px" }}
            maxW={{ base: "100%", md: "300px" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="100%"
          >
            <Image
              src="/png/ossp_1.png"
              alt="OSSP Platform Illustration"
              maxW="100%"
              height="auto"
              borderRadius="lg"
              loading="lazy"
              fallbackSrc="/png/default-product.png"
            />
          </Box>
        </HStack>
      </Container>
      <Box py={{ base: 12, md: 16 }} position="relative" overflow="hidden">
        <Container maxW="7xl">
          <VStack spacing={6} align="center" textAlign="center">
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="#000B8C"
              lineHeight="1.2"
            >
              Why OSSP{" "}
              <Text as="span" color="gray.700">
                Stands Out
              </Text>
            </Heading>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color="gray.500"
              lineHeight="1.2"
              textAlign="center"
              maxW="4xl"
              px={{ base: 4, md: 0 }}
            >
              Based on our framework-based approach, OSSP dramatically reduces
              process time and operational cost—delivering measurable gains in
              efficiency and transparency.
            </Text>
          </VStack>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={8}
            w="100%"
          >
            <Image
              src="/png/ossp.png"
              alt="OSSP asset"
              maxW={{ base: "90%", md: "1000px" }}
              w="100%"
              borderRadius="lg"
              loading="lazy"
              fallbackSrc="/png/default-product.png"
            />
          </Box>
        </Container>
      </Box>

      {/* Core Features Section */}
      <Container maxW="7xl" py={{ base: 8, md: 12 }}>
        <VStack spacing={8} align="center">
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="#000B8C"
            lineHeight="1.2"
          >
            Core{" "}
            <Text as="span" color="gray.700">
              Features
            </Text>
          </Heading>

          {/* Features Grid */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={6}
            w="100%"
            mt={8}
            justifyItems="center"
          >
            {coreFeatures.map((feature, index) => (
              <GridItem
                key={index}
                w="100%"
                maxW="400px"
                colSpan={
                  // Center the last card if it's alone in the last row
                  index === coreFeatures.length - 1 &&
                  coreFeatures.length % 3 === 1 &&
                  coreFeatures.length > 3
                    ? { base: 1, md: 2, lg: 3 }
                    : 1
                }
                justifySelf={
                  // Center the last card if it's alone in the last row
                  index === coreFeatures.length - 1 &&
                  coreFeatures.length % 3 === 1 &&
                  coreFeatures.length > 3
                    ? "center"
                    : "stretch"
                }
              >
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              </GridItem>
            ))}
          </Grid>
        </VStack>
      </Container>
      {/* Why Choose OSS Platform Section */}
      <CommonFeatureCard
        title="Why Choose OSS Platform with Business Automation"
        features={whyChooseFeatures}
      />
      {/* Ideal For Section */}
      <Container maxW="7xl" py={{ base: 8, md: 12 }}>
        <VStack spacing={8} align="center">
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="bold"
            color="#000B8C"
            textAlign="center"
          ></Heading>
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="bold"
            color="#000B8C"
            lineHeight="1.2"
          >
            Ideal{" "}
            <Text as="span" color="gray.700">
              for
            </Text>
          </Heading>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={6}
            w="100%"
            mt={8}
            justifyContent="center"
            alignItems="stretch"
          >
            {idealForServices.map((service) => (
              <Box
                key={service.id}
                w="100%"
                maxW="400px"
                minW={{ base: "100%", md: "300px" }}
                flex={{
                  base: "1 1 100%",
                  md: "1 1 calc(50% - 12px)",
                  lg: "1 1 calc(33.333% - 16px)",
                }}
              >
                <ServiceCard service={service} />
              </Box>
            ))}
          </Box>
        </VStack>
      </Container>
    </Box>
    </>
  );
};

export default OSSPPage;
